import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip
} from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';
import { calculateCablePrice, formatPrice } from '../services/pricingService';

export default function CompactPriceDisplay({ config, showIcon = true, variant = 'body2' }) {
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Memoize config to prevent unnecessary recalculations
  const memoizedConfig = useMemo(() => {
    console.log('DEBUG: CompactPriceDisplay memoizedConfig recalculated');
    console.log('DEBUG: CompactPriceDisplay config:', config);
    console.log('DEBUG: CompactPriceDisplay config.hybridConnectors:', config?.hybridConnectors);
    return config;
  }, [config]);

  // Create a stable safeConfig without using getSafeConfig to avoid object recreation
  const memoizedSafeConfig = useMemo(() => {
    if (!memoizedConfig || !memoizedConfig.type) return null;
    
    // For hybrid connectors, use the overall length from hybridConnectors.lengths.A (in inches)
    // For standard cables, convert feet to inches for pricing service
    let pricingLength = memoizedConfig.length;
    
    if (memoizedConfig.hybridConnectors?.lengths?.A) {
      // Hybrid connectors store lengths in inches, use directly
      pricingLength = memoizedConfig.hybridConnectors.lengths.A;
    } else if (memoizedConfig.length && !['hybrid', 'zip', 'zip-audio-audio', 'wye', 'hybrid-power-audio-audio'].includes(memoizedConfig.style)) {
      // Standard cables store length in feet, convert to inches for pricing
      pricingLength = Math.round(memoizedConfig.length * 12);
    }
    // For hybrid connectors, length is already in inches from CableBuilder
    
    // Create the safe config structure directly here to avoid object recreation
    return {
      category: memoizedConfig.category || '',
      type: memoizedConfig.type,
      style: memoizedConfig.style || 'end-to-end',
      length: pricingLength, // Use the converted length in inches
      quality: memoizedConfig.quality || '',
      endA: memoizedConfig.connectors?.endA || {},
      endB: memoizedConfig.connectors?.endB || {},
      endC: memoizedConfig.connectors?.endC || {},
      finishes: memoizedConfig.finishing || {},
      hybridConnectors: memoizedConfig.hybridConnectors || {},
      wyeConnectors: memoizedConfig.wyeConnectors || {},
      wireGauge: memoizedConfig.wireGauge || {}
    };
  }, [memoizedConfig]);

  useEffect(() => {
    let isMounted = true;
    
    const calculatePrice = async () => {
      if (!memoizedSafeConfig) {
        if (isMounted) {
          setPriceInfo(null);
          setError('');
        }
        return;
      }

      setLoading(true);
      setError('');
      
      try {
        console.log('DEBUG: CompactPriceDisplay safeConfig for pricing:', memoizedSafeConfig);
        const result = await calculateCablePrice(memoizedSafeConfig);
        if (isMounted) {
          setPriceInfo(result);
        }
      } catch (error) {
        console.error('Error calculating price:', error);
        if (isMounted) {
          setError('Price unavailable');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    calculatePrice();
    
    return () => {
      isMounted = false;
    };
  }, [memoizedSafeConfig]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {showIcon && <MoneyIcon fontSize="small" color="action" />}
        <CircularProgress size={12} />
      </Box>
    );
  }

  if (error || !priceInfo) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {showIcon && <MoneyIcon fontSize="small" color="action" />}
        <Typography variant={variant} color="text.secondary">
          {error || 'Price unavailable'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {showIcon && <MoneyIcon fontSize="small" color="primary" />}
      <Typography variant={variant} fontWeight="medium" color="primary">
        {formatPrice(priceInfo.totalCost)}
      </Typography>
    </Box>
  );
}

// Mini price chip for very compact displays
export function MiniPriceChip({ config }) {
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const calculatePrice = async () => {
      if (!config || !config.type) return;

      setLoading(true);
      
      try {
        const result = await calculateCablePrice(config);
        if (isMounted) {
          setPriceInfo(result);
        }
      } catch (error) {
        console.error('Error calculating price:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    calculatePrice();
    
    return () => {
      isMounted = false;
    };
  }, [config]);

  if (loading) {
    return (
      <Chip 
        label="..." 
        size="small" 
        variant="outlined"
        sx={{ minWidth: 60 }}
      />
    );
  }

  if (!priceInfo) {
    return (
      <Chip 
        label="N/A" 
        size="small" 
        variant="outlined"
        color="default"
        sx={{ minWidth: 60 }}
      />
    );
  }

  return (
    <Chip 
      label={formatPrice(priceInfo.totalCost)} 
      size="small" 
      color="primary"
      variant="filled"
      sx={{ minWidth: 60, fontWeight: 'medium' }}
    />
  );
} 